import { zodResolver } from "@hookform/resolvers/zod";
import type { AxiosError } from "axios";
import Axios from "axios";
import { useContext, useEffect, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import type { TFunction } from "react-i18next";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import * as z from "zod";
import { PrimaryButtonLargeV2 } from "../../../../components/Buttons/Buttons";
import { DatePicker } from "../../../../components/DatePicker/DatePicker";
import { DelayedSpinner } from "../../../../components/DelayedSpinner/DelayedSpinner";
import { ErrorPlaceholder } from "../../../../components/Error";
import { Notifications } from "../../../../components/Notifications/NotificationsContext";
import { SelectBoxV2 } from "../../../../components/SelectBoxV2/SelectBoxV2";
import { TextField } from "../../../../components/TextFields/TextFields";
import {
  H4,
  H6,
  SmallText,
} from "../../../../components/Typography/Typography";
import { endpoints } from "../../../../endpoints";
import { Form } from "../../../../layout/FormLayout";
import type {
  DataMutate,
  Language,
  OptionType,
  SupportedLanguage,
} from "../../../../types/types";
import type {
  ListDetailsSchema,
  ListItemObjectSchema,
  PIMProduct,
  SupportedAssetAccessibility,
  SupportedAssetCategoryType,
  SupportedAssetType,
} from "../../../../types/types.PIM";
import { useCountriesList, useRegionsList } from "../../../../util/Locations";
import { stringIsNotOnlyWhiteSpaceRegex } from "../../../../util/regexes";
import { strings } from "../../../../util/strings";
import {
  getDateTime,
  toTitleCase,
  useFormWrapper,
  useStoreState,
  useUpdateProductStatus,
} from "../../../../util/util";
import {
  zodOptionalSelectBoxDefault,
  zodOptionalString,
  zodSelectBoxDefault,
  zodSelectBoxType,
} from "../../../../util/zod.util";
import type {
  AssetCategory,
  AssetType,
} from "../../../admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/AssetsUtil";
import { FileUploader } from "../../../admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/FileUploader";
import { RadioButton } from "../../../../components/RadioButton/RadioButton";
import { DocumentAccessibility } from "../../../../components/DocumentAccessibility/DocumentAccessibility";
import { accessibilityRequestData } from "./util";

export interface IAssetPayload {
  name: string;
  asset_type: string;
  category_id: string;
  language?: string;
  product_ids?: string[];
  document_type?: string | null;
  issue_date?: string | null;
  expiration_date?: string | null;
  renewal_date?: string | null;
  country?: string | null;
  region?: string | null;
  visibility?: string;
  role_ids?: string[];
  user_ids?: string[];
  customer_ids?: string[];
  allow_all_customers?: boolean;
  allow_all_distributors?: boolean;
  allow_all_roles?: boolean;
}

const AddAssetSchema = z.object({
  asset_name: z.string(),
  file_type: z.instanceof(File),
  input_asset_type: zodSelectBoxType,
  input_category_type: zodSelectBoxType,
  input_lang_type: zodSelectBoxType,
  input_document_type: zodSelectBoxType.optional().nullable(),
  issue_date: zodOptionalString,
  expiration_date: zodOptionalString,
  renewal_date: zodOptionalString,
  country: zodSelectBoxType.optional().nullable(),
  region: zodSelectBoxType.optional().nullable(),
  product_id: zodSelectBoxType.optional(),
  custom_category_name: zodOptionalString,
  visibility: z.string(),
  input_customer_ids: z.array(z.string()).optional().nullable(),
  input_user_ids: z.array(z.string()).optional().nullable(),
  input_role_ids: z.array(z.string()).optional().nullable(),
});

type FormValues = z.infer<typeof AddAssetSchema>;

const AddAssetSchemaFn = (t: TFunction) =>
  z
    .object({
      asset_name: z
        .string()
        .regex(stringIsNotOnlyWhiteSpaceRegex, t("This is a required field")),
      input_asset_type: zodSelectBoxDefault(t),
      file_type: z.instanceof(File),
      input_category_type: zodSelectBoxDefault(t),
      input_lang_type: zodSelectBoxDefault(t),
      product_id: zodOptionalSelectBoxDefault.optional(),
      issue_date: zodOptionalString,
      expiration_date: zodOptionalString,
      renewal_date: zodOptionalString,
      input_document_type: zodOptionalSelectBoxDefault.optional().nullable(),
      country: zodOptionalSelectBoxDefault.optional().nullable(),
      region: zodOptionalSelectBoxDefault.optional().nullable(),
      visibility: z.string().optional(),
      custom_category_name: z
        .string()
        .refine((val) => Boolean(val.trim()), {
          message: strings(t).thisIsARequiredField,
        })
        .nullish(),
      input_role_ids: z.array(z.string()).optional().nullable(),
      input_user_ids: z.array(z.string()).optional().nullable(),
      input_customer_ids: z.array(z.string()).optional().nullable(),
    })
    .superRefine(({ input_asset_type, input_category_type }, ctx) => {
      if (!input_asset_type?.value || !input_category_type?.value) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("This is a required field"),
          path: ["input_asset_type"],
        });
      }
    });

export const NewAssetForm = <ParentData,>({
  onComplete,
  product,
  products,
  refreshParentData,
  defaultAssetType,
  onAddAssetData,
  default_asset_types,
  default_categories,
  hideAccessibility,
}: {
  defaultAssetType?: string;
  onComplete?: (productId?: string) => void;
  product?: PIMProduct;
  products?: OptionType<string>[];
  refreshParentData: DataMutate<ParentData>;
  onAddAssetData?: (data: FormValues) => Promise<void>;
  default_asset_types?: OptionType<SupportedAssetType>[];
  default_categories?: OptionType<SupportedAssetCategoryType>[];
  hideAccessibility?: boolean;
}) => {
  const { t } = useTranslation();
  const countries = useCountriesList();
  const regions = useRegionsList();
  const [submitting, setSubmitting] = useState(false);
  const [assetTypes, setAssetTypes] = useState<
    OptionType<SupportedAssetType>[]
  >([]);
  const [assetCategories, setAssetCategories] = useState<
    OptionType<SupportedAssetCategoryType>[]
  >([]);

  const [documentTypesFilters, setDocumentTypesFilters] = useState<
    OptionType<string>[]
  >([]);

  const [assetLanguages, setAssetLanguages] = useState<
    OptionType<SupportedLanguage>[]
  >([]);

  const { notifySuccess, notifyError } = useContext(Notifications);
  const { tenant_id, storefront_id } = useStoreState();

  const updateProductStatus = useUpdateProductStatus({
    product: product ?? ({ status: "archived" } as PIMProduct),
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [accessibility, setAccessibility] =
    useState<SupportedAssetAccessibility>("internal");

  const assetTypeDefault = useMemo(
    () =>
      defaultAssetType === "Documents"
        ? { label: t("Document"), value: "document" }
        : null,
    [defaultAssetType, t]
  );

  const methodsOfUseForm = useFormWrapper({
    resolver: zodResolver(AddAssetSchemaFn(t)),
    defaultValues: {
      input_asset_type: assetTypeDefault ?? {
        label: undefined,
        value: undefined,
      },
      custom_category_name: null,
      input_category_type: { label: undefined, value: undefined },
      input_lang_type:
        assetLanguages.length === 1
          ? assetLanguages[0]
          : { label: undefined, value: undefined },
      input_document_type: undefined,
      product_id:
        products?.length === 1
          ? products[0]
          : { label: undefined, value: undefined },
      file_type: undefined,
      asset_name: undefined,
      visibility: accessibility,
      input_role_ids: [],
      input_user_ids: [],
      input_customer_ids: [],
    },
  });

  const {
    handleSubmit,
    control,
    errors,
    formState,
    register,
    setValue,
    watch,
  } = methodsOfUseForm;

  const selectedAssetType: string = watch("input_asset_type")?.value;

  const selectedCategoryType: string = watch("input_category_type")?.value;

  const { data: assetTypeResponse, error: assetTypeError } = useSWR<AssetType>(
    !default_asset_types ? endpoints.v2_storefronts_id_pim_asset_types() : null
  );

  const { error: categoryTypeError } = useSWR<AssetCategory>(
    selectedAssetType && !default_categories
      ? `${endpoints.v2_tenants_id_pim_assets_categories(
          tenant_id
        )}?asset_type=${assetTypeDefault?.value ?? selectedAssetType}`
      : null,
    {
      onSuccess: ({ asset_categories }) =>
        setAssetCategories(
          asset_categories.map((category) => ({
            label: category.name,
            value: category.id,
          }))
        ),
    }
  );

  const { data: documentTypesResponse, error: documentTypeError } = useSWR<{
    data: ListDetailsSchema;
  }>(
    endpoints.v2_tenants_id_pim_get_list_by_name(tenant_id, "Document Types"),
    {
      onError: (error) => {
        console.error("There was an error fetching the Document types list.", {
          error,
        });
      },
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: true,
      onSuccess: (data) => {
        setDocumentTypesFilters(
          data.data.items.map((type: ListItemObjectSchema) => {
            return { label: type.name, value: type.name };
          })
        );
      },
    }
  );

  const { data: languagesResponse, error: languageError } = useSWR<{
    languages: Language[];
  }>(endpoints.v1_storefronts_id_languages(storefront_id), {
    onSuccess: ({ languages }) => {
      setAssetLanguages(
        languages.map((item) => ({
          label: `${item.name} (${item.alpha_2.toUpperCase()})`,
          value: item.alpha_2,
        }))
      );
    },
    onError: () =>
      notifyError(t("could not load asset languages, Something went wrong.")),
  });

  useEffect(() => {
    if (languagesResponse) {
      setAssetLanguages(
        languagesResponse.languages.map((item) => ({
          label: `${item.name} (${item.alpha_2.toUpperCase()})`,
          value: item.alpha_2,
        }))
      );
    }
  }, [languagesResponse]);

  useEffect(() => {
    if (documentTypesResponse) {
      setDocumentTypesFilters(
        documentTypesResponse.data.items.map((type: ListItemObjectSchema) => {
          return { label: type.name, value: type.name };
        })
      );
    }
  }, [documentTypesResponse]);

  const default_submit = async (formValues: FormValues) => {
    const {
      asset_name,
      input_asset_type,
      input_category_type,
      issue_date,
      expiration_date,
      renewal_date,
      country,
      region,
      input_lang_type,
      input_document_type,
      input_customer_ids,
      input_user_ids,
      input_role_ids,
    } = formValues;

    let updatedProduct = null;
    if (product?.id) {
      const { data } = await updateProductStatus();
      updatedProduct = data;
    }

    const assetType =
      input_asset_type?.value.toLowerCase() as SupportedAssetType;
    const requestPayload: IAssetPayload & {
      is_downloadable?: boolean;
      visibility: SupportedAssetAccessibility;
    } = {
      name: asset_name,
      asset_type: assetType,
      category_id: input_category_type?.value,
      language: input_lang_type?.value,
      visibility: accessibility,
      document_type: input_document_type?.value || null,
      ...accessibilityRequestData(
        assetType,
        accessibility,
        input_customer_ids,
        input_user_ids,
        input_role_ids
      ),
    };

    if (product?.id) {
      requestPayload.product_ids = [updatedProduct!.id];
    }
    if (issue_date) {
      requestPayload.issue_date = getDateTime(issue_date, true).dateFullYear;
    }
    if (expiration_date) {
      requestPayload.expiration_date = getDateTime(
        expiration_date,
        true
      ).dateFullYear;
    }
    if (renewal_date) {
      requestPayload.renewal_date = getDateTime(
        renewal_date,
        true
      ).dateFullYear;
    }
    if (input_document_type?.value) {
      requestPayload.document_type = input_document_type?.value;
    }
    if (country?.value) {
      requestPayload.country = country?.value;
    }
    if (region?.value) {
      requestPayload.region = region?.value;
    }

    const baseURL = endpoints.v2_storefronts_id_pim_assets(tenant_id);

    setSubmitting(true);

    try {
      const { data: createResp } = await Axios.post(baseURL, requestPayload);
      const assetId = createResp?.id ?? createResp?.data?.id;
      if (selectedFile && assetId) {
        const uploadUrl = endpoints.v2_tenants_id_pim_assets_id(
          tenant_id,
          assetId
        );
        const formData = new FormData();
        formData.append("file", selectedFile);
        await Axios.put(uploadUrl, formData);
      }
      await refreshParentData();
      notifySuccess(t("Asset successfully added"));
      if (onComplete) {
        onComplete(
          updatedProduct?.primary_staged_product_id ?? updatedProduct?.id
        );
      }
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t("Could not add asset. Something went wrong."),
        {
          error,
        }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const changeAssetAccessibility = (
    newAccessibility: string | React.FormEvent<HTMLSelectElement>
  ) => {
    if (typeof newAccessibility === "string") {
      setAccessibility(newAccessibility as SupportedAssetAccessibility);
    } else if (newAccessibility.currentTarget.value) {
      setAccessibility(
        newAccessibility.currentTarget.value as SupportedAssetAccessibility
      );
    }
  };

  const onSubmit = async (formValues: FormValues): Promise<void> => {
    if (onAddAssetData) {
      setSubmitting(true);
      await onAddAssetData(formValues);
      setSubmitting(false);
    } else {
      default_submit(formValues);
    }
  };

  useEffect(() => {
    setValue("input_category_type", { label: undefined, value: undefined });
    setValue("file_type", undefined);
    setValue("asset_name", undefined);
    setSelectedFile(null);
  }, [selectedAssetType, setValue]);

  useEffect(() => {
    if (assetTypeResponse) {
      const { asset_types } = assetTypeResponse;
      setAssetTypes(
        product?.id
          ? !assetTypeDefault
            ? asset_types.reduce<OptionType<SupportedAssetType>[]>(
                (acc, cur) =>
                  cur === "document"
                    ? acc
                    : [
                        ...acc,
                        {
                          label: toTitleCase(cur),
                          value: cur,
                        },
                      ],
                []
              )
            : []
          : asset_types.map((type) => ({
              label: toTitleCase(type),
              value: type,
            }))
      );
    }
  }, [assetTypeDefault, assetTypeResponse, product?.id]);

  const isLoadingAssetTypes =
    !assetTypeError && !assetTypeResponse && !default_asset_types;

  if (isLoadingAssetTypes) {
    return <DelayedSpinner />;
  }

  if (assetTypeError || categoryTypeError) {
    return (
      <ErrorPlaceholder
        message={t(
          "There was an error loading asset types and categories. Please try again later"
        )}
      />
    );
  }

  return (
    <>
      {
        product?.id ? (
          <div style={{ margin: "24px 0px 16px 0px" }}>
            <H6>{t("Asset details")}</H6>
          </div>
        ) : // TODO externalize titles
        null
        // <MarginBottomHeaderLeft>
        //   <SectionTitle>{t("Add Asset")}</SectionTitle>
        // </MarginBottomHeaderLeft>
      }
      <Form id="new_asset_form_id" onSubmit={handleSubmit(onSubmit)}>
        <Controller
          as={SelectBoxV2}
          control={control}
          name="input_asset_type"
          autoComplete={"input_asset_type"}
          placeholder={t("Asset Type")}
          rules={{
            required: true,
          }}
          isDisabled={product?.id && !!assetTypeDefault}
          errors={errors}
          formState={formState}
          defaultValue={
            assetTypeDefault ?? { label: undefined, value: undefined }
          }
          options={default_asset_types ?? assetTypes}
        />
        <Controller
          as={SelectBoxV2}
          control={control}
          name="input_category_type"
          autoComplete={"input_category_type"}
          placeholder={t("Category")}
          options={default_categories ?? assetCategories}
          rules={{
            required: true,
          }}
          errors={errors}
          isDisabled={!selectedAssetType}
          formState={formState}
        />
        {selectedCategoryType && selectedCategoryType === "other" ? (
          <TextField
            name="custom_category_name"
            label={t("Description")}
            autoComplete={"custom_category_name"}
            theref={register()}
            formState={formState}
            errors={errors}
            type="text"
          />
        ) : (
          <></>
        )}
        <FileUploader
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          setValue={setValue}
          selectedAssetType={selectedAssetType}
          control={control}
          errors={errors}
          formState={formState}
        />
        <TextField
          name="asset_name"
          label={t("Asset Name")}
          autoComplete={"asset_name"}
          theref={register({
            required: true,
          })}
          formState={formState}
          errors={errors}
          type="text"
        />
        {!languageError && (
          <Controller
            as={SelectBoxV2}
            control={control}
            options={assetLanguages}
            name="input_lang_type"
            autoComplete={"input_lang_type"}
            placeholder={t("Language")}
            rules={{
              required: true,
            }}
            errors={errors}
            formState={formState}
          />
        )}
        {!documentTypeError && (
          <Controller
            as={SelectBoxV2}
            control={control}
            name="input_document_type"
            autoComplete={"input_document_type"}
            isClearable={true}
            placeholder={t("Document Type")}
            options={documentTypesFilters}
            errors={errors}
            formState={formState}
          />
        )}
        <DatePicker
          label={t("Issue Date")}
          name={"issue_date"}
          required={false}
          methodsOfUseForm={methodsOfUseForm}
          placeholder={t("MMM DD, YYYY")}
          displayFormat="MMM DD, YYYY"
          isOutsideRange={(day) => false}
        />
        <DatePicker
          label={t("Expiration Date")}
          name={"expiration_date"}
          required={false}
          methodsOfUseForm={methodsOfUseForm}
          placeholder={t("MMM DD, YYYY")}
          displayFormat="MMM DD, YYYY"
          isOutsideRange={(day) => false}
        />
        <DatePicker
          label={t("Renewal Date")}
          name={"renewal_date"}
          required={false}
          methodsOfUseForm={methodsOfUseForm}
          placeholder={t("MMM DD, YYYY")}
          displayFormat="MMM DD, YYYY"
          isOutsideRange={(day) => false}
        />
        <Controller
          as={SelectBoxV2}
          control={control}
          name="country"
          autoComplete={"country"}
          placeholder={t("Country")}
          isClearable={true}
          options={countries}
          rules={{
            required: false,
          }}
          errors={errors}
          formState={formState}
        />
        <Controller
          as={SelectBoxV2}
          control={control}
          name="region"
          autoComplete={"region"}
          isClearable={true}
          placeholder={t("Region")}
          options={regions}
          rules={{
            required: false,
          }}
          errors={errors}
          formState={formState}
        />
        {Boolean(products) && products!.length > 0 && (
          <Controller
            as={SelectBoxV2}
            control={control}
            name="product_id"
            autoComplete={"products"}
            placeholder={t("Assign to product")}
            options={products}
            rules={{
              required: false,
            }}
            errors={errors}
            formState={formState}
          />
        )}
        {defaultAssetType &&
          !hideAccessibility &&
          (selectedAssetType === "document" ? (
            <DocumentAccessibility
              accessibility={accessibility}
              methodsOfUseForm={methodsOfUseForm}
              onAccessibilityChange={changeAssetAccessibility}
              existingData={{
                users: [],
                roles: [{ id: "allow_all_roles", name: "All Roles" }],
                customers: [
                  { id: "allow_all_customers", name: "All Customers" },
                  { id: "allow_all_distributors", name: "All Distributors" },
                ],
              }}
            />
          ) : (
            <>
              <H4>{t("Accessibility")}</H4>
              <SmallText>{t("Media is accessible by:")}</SmallText>
              <div>
                <span style={{ display: "inline-block" }}>
                  <RadioButton
                    name={"public"}
                    value="public"
                    checked={accessibility === "public"}
                    optionTitle={t("Public")}
                    handleChange={changeAssetAccessibility}
                    style={{ marginBottom: "15px" }}
                  />
                </span>
              </div>
              <div>
                <span style={{ display: "inline-block" }}>
                  <RadioButton
                    name={"logged_in_user"}
                    value="logged_in_user"
                    checked={accessibility === "logged_in_user"}
                    optionTitle={t("Customers & Internal Users")}
                    handleChange={changeAssetAccessibility}
                    style={{ marginBottom: "15px" }}
                  />
                </span>
              </div>
              <div>
                <span style={{ display: "inline-block" }}>
                  <RadioButton
                    name={"internal"}
                    value="internal"
                    checked={accessibility === "internal"}
                    optionTitle={t("Internal Users Only")}
                    handleChange={changeAssetAccessibility}
                    style={{ marginBottom: "15px" }}
                  />
                </span>
              </div>
            </>
          ))}
        <PrimaryButtonLargeV2 fullWidth loading={submitting} type="submit">
          {t("Save")}
        </PrimaryButtonLargeV2>
      </Form>
    </>
  );
};
